// Notifications: the bell list and urgent pop-ups on the site.
// ORCA API: the bell is the account's real events (GET /notifications), polled
// every minute while the tab is visible and again when the user comes back to
// it; a new urgent one (a Price Alert) pops up on the site. No browser / system
// notifications (product decision). Read state is Web only.
// DEV mock: live events from `noticesApi.subscribe`.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import i18n from '@/i18n';
import { api, noticesApi } from '@/lib/api/client';
import { MOCK_DATA } from '@/lib/api/mode';
import type { Alerts, MarketData, Notice } from '@/types';
import type { LiveEvent } from '@/mock/alerts';

type Ctx = {
  notices: Notice[];
  unread: number;
  popups: Notice[];
  open: (n: Notice) => void;
  markAll: () => void;
  toggleRead: (id: string) => void;
  dismiss: (id: string) => void;
  test: () => void;
};
const NoticesCtx = createContext<Ctx | null>(null);
export const useNotices = () => {
  const c = useContext(NoticesCtx);
  if (!c) throw new Error('useNotices outside NoticesProvider');
  return c;
};

export function NoticesProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: notices = [] } = useQuery({
    queryKey: ['notices'], queryFn: api.notices,
    // a hidden tab does not poll (TanStack default); coming back refreshes once
    refetchInterval: MOCK_DATA ? false : 60_000, refetchOnWindowFocus: MOCK_DATA ? false : 'always',
  });
  const [popups, setPopups] = useState<Notice[]>([]);

  const setRead = useCallback((ids: string[] | 'all') => {
    qc.setQueryData<Notice[]>(['notices'], (xs) => xs?.map((x) => (ids === 'all' || ids.includes(x.id) ? { ...x, read: true } : x)));
    noticesApi.markRead(ids).catch(() => {});
  }, [qc]);

  const popUp = useCallback((n: Notice) => {
    // no browser / system notifications (product decision): the site shows its own events
    const ch = qc.getQueryData<Alerts>(['alerts'])?.channels ?? { site: true };
    if (ch.site) setPopups((ps) => [n, ...ps].slice(0, 3));
  }, [qc]);

  const deliver = useCallback((e: LiveEvent) => {
    const n = e.notice;
    qc.setQueryData<Notice[]>(['notices'], (xs) => (xs?.some((x) => x.id === n.id) ? xs : [n, ...(xs ?? [])]));
    if (e.price || e.whale) {
      qc.setQueryData<Alerts>(['alerts'], (a) => a && {
        ...a,
        price: e.price ? { ...a.price, history: [e.price, ...a.price.history], coins: a.price.coins.map((c) => (c.symbol === e.price!.symbol ? { ...c, level: e.price!.level } : c)) } : a.price,
        whale: e.whale ? { ...a.whale, feed: [e.whale, ...a.whale.feed] } : a.whale,
      });
    }
    if (e.coin) {
      const c = e.coin;
      qc.setQueryData<MarketData>(['market'], (m) => m && { ...m, universe: m.universe.map((x) => (x.symbol === c.symbol ? { ...x, change24h: c.change, price: c.price } : x)) });
    }
    if (!n.urgent) return;
    popUp(n);
  }, [qc, popUp]);


  // ORCA API: urgent notices that appeared since the last poll pop up (never the ones already there on load)
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (MOCK_DATA) return;
    if (seen.current === null) { if (notices.length || qc.getQueryState(['notices'])?.status === 'success') seen.current = new Set(notices.map((n) => n.id)); return; }
    for (const n of notices) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (n.urgent && !n.read) popUp(n);
    }
  }, [notices, popUp, qc]);

  useEffect(() => {
    let off: (() => void) | undefined;
    let alive = true;
    noticesApi.subscribe(deliver).then((u) => { if (alive) off = u; else u(); });
    return () => { alive = false; off?.(); };
  }, [deliver]);

  const dismiss = useCallback((id: string) => setPopups((ps) => ps.filter((p) => p.id !== id)), []);
  const open = useCallback((n: Notice) => { setRead([n.id]); dismiss(n.id); navigate(n.to); }, [setRead, dismiss, navigate]);
  const markAll = useCallback(() => setRead('all'), [setRead]);
  const toggleRead = useCallback((id: string) => qc.setQueryData<Notice[]>(['notices'], (xs) => xs?.map((x) => (x.id === id ? { ...x, read: !x.read } : x))), [qc]);
  // ORCA API: a plainly marked test pop-up, never a made-up alert
  const test = useCallback(() => {
    if (!MOCK_DATA) {
      popUp({ id: `test-${Date.now()}`, kind: 'price', urgent: true, title: i18n.t('notices:real.testTitle'), body: i18n.t('notices:real.testBody'),
        at: new Date().toISOString(), read: true, to: '/alerts' });
      return;
    }
    noticesApi.test().then(deliver);
  }, [deliver, popUp]);

  const value = useMemo<Ctx>(() => ({ notices, unread: notices.filter((x) => !x.read).length, popups, open, markAll, toggleRead, dismiss, test }),
    [notices, popups, open, markAll, toggleRead, dismiss, test]);
  return <NoticesCtx.Provider value={value}>{children}</NoticesCtx.Provider>;
}
